import React, { FC, useMemo, useRef, useState } from 'react'
import { Box, Modal, PrimaryButton } from '@moonpig/launchpad-components'
import { trackGAEvent } from '@moonpig/web-core-analytics'
import { useQuery } from '@moonpig/web-core-graphql'
import { useRouter } from '@moonpig/web-core-routing'
import { useStoreId } from '@moonpig/web-core-stores'
import { useFindLocaleText } from '../../../text-localisation'
import { ProductModuleSection } from '../ProductModuleSection'
import { ProductModule } from '../types'
import { useProductContext } from '../../../contexts/product'
import { GetBasketGQL } from '../../../queries/getBasketGQL'
import { GetBasketQuery } from '../../../queries/__generated__/getBasketGQL'
import {
  PageTypeEnum,
  ProductStoreProvider,
  SelectCardModule,
  useBasket,
} from '@moonpig/web-shared-products'

export const SelectCardCTAModule: FC<ProductModule> = props => {
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const localiseText = useFindLocaleText()
  const [{ product, selectedOptions }] = useProductContext()
  const [_, { setSelectedAmountWarning }] = useBasket()
  const selectedVariantKey = selectedOptions?.variantKey
  const storeId = useStoreId()
  const router = useRouter()

  const { data: basketData, loading: basketLoading } =
    useQuery<GetBasketQuery>(GetBasketGQL)

  const hasCards = useMemo(() => {
    const lineItems = basketData?.me.basket?.lineItems ?? []
    return lineItems.some(
      item => item.productType === 'greetingcard' && item.design !== null,
    )
  }, [basketData])

  const handleChooseNewCard = () => {
    trackGAEvent({
      event: 'select_content',
      event_data: {
        action: 'select new card',
        category: 'product details',
        label: 'select card module | select new',
      },
    })

    router.push({
      name: 'add-a-card',
      params: {
        region: storeId,
        version: 1,
        digitalGift: product.id,
      },
    })
  }

  const handleCTAClick = () => {
    const selectedVariant = product.variants.find(
      v => v.sku === selectedVariantKey || v.key === selectedVariantKey,
    )

    if (!selectedVariant) {
      setSelectedAmountWarning(true)
      return
    }

    if (hasCards) {
      setIsOpen(true)
      return
    }

    handleChooseNewCard()
  }

  return (
    <ProductModuleSection {...props}>
      <ProductStoreProvider
        key={selectedVariantKey}
        initialProduct={product}
        initialVariantKey={selectedVariantKey}
      >
        <PrimaryButton
          ref={triggerRef}
          width={1}
          onClick={handleCTAClick}
          loading={basketLoading}
        >
          {localiseText('find.add_gift_to_a_card')}
        </PrimaryButton>
        <Modal
          isOpen={isOpen}
          label={localiseText('find.select_card_modal_label')}
          hasCloseButton
          mobilePagePeek
          onDismiss={() => setIsOpen(false)}
          triggerRef={triggerRef}
        >
          <Box width={1} maxWidth="550px" marginX="auto">
            {isOpen && (
              <SelectCardModule
                module={{ __typename: 'ModuleSelectCard' }}
                context={{ key: 0, pageType: PageTypeEnum.PDP }}
              />
            )}
          </Box>
        </Modal>
      </ProductStoreProvider>
    </ProductModuleSection>
  )
}
